import Handler from "./Handler";

// Shared Razorpay Checkout.js widget for the Admin frontend - mirrors
// Customer/customer_frontend/src/Pages/Booking/Checkout.js's own
// loadRazorpayScript/new window.Razorpay(...)/handler/on("payment.failed")
// integration (the only other place in this codebase that actually opens a
// real Checkout session), wrapped as a single promise-returning helper so
// every Admin consumer (Phase B - Extension now, Phase E - Settlement
// later) shares one implementation instead of copy-pasting it.

const SITE_NAME = "Getmydrive";
const THEME_COLOR = "#44084e"; // matches Customer Checkout.js's own theme

let scriptPromise = null;

export const loadRazorpayScript = () => {
    if (window.Razorpay) return Promise.resolve(true);
    if (!scriptPromise) {
        scriptPromise = new Promise((resolve) => {
            const script = document.createElement("script");
            script.src = "https://checkout.razorpay.com/v1/checkout.js";
            script.onload = () => resolve(true);
            script.onerror = () => {
                scriptPromise = null; // let a later call retry instead of caching a failure forever
                resolve(false);
            };
            document.body.appendChild(script);
        });
    }
    return scriptPromise;
};

// Live publishable key from Settings > Payment (admin/booking/payment-key) -
// not a build-time env var, since an admin can change it there without a
// redeploy.
//
// Test mode: pass the bookingId and the server answers with the key of THAT booking's Razorpay account (a test
// booking is paid in the test account and its order only opens with the test key). { key, mode } or null.
export const fetchRazorpayKey = async (bookingId) => {
    const response = await Handler({ method: "get", url: "/staff/flow/payment-key", ...(bookingId ? { params: { bookingId } } : {}) });
    if (!response?.success) return null;
    const data = response?.data?.data || {};
    return { key: data.razorpayKey || null, mode: data.paymentMode === "test" ? "test" : "live" };
};

// A test booking only ever opens with a test key id; anything else is refused before the window opens.
export const usableKey = (found) => {
    const key = String(found?.key || "");
    if (found?.mode === "test") return /^rzp_test_[A-Za-z0-9]+$/.test(key) ? key : null;
    return /^rzp_(live|test)_[A-Za-z0-9]+$/.test(key) ? key : null;
};

// Opens a Checkout session against an order that's already been created
// server-side (orderId + amount, same rupee amount the order itself was
// created for). Resolves rather than throwing/callback-ing, so callers can
// just `await` it:
//   { status: "success", response: { razorpay_payment_id, razorpay_signature, ... } }
//   { status: "dismissed" }  - the admin closed the widget without paying
//   { status: "failed" }     - Razorpay itself reported payment.failed
//   { status: "error", message } - script/key couldn't be loaded at all
// bookingId: the booking the order belongs to (test mode picks that booking's Razorpay account).
export const openRazorpayCheckout = async ({ orderId, amount, description, prefill, bookingId }) => {
    const [scriptLoaded, found] = await Promise.all([loadRazorpayScript(), fetchRazorpayKey(bookingId)]);
    const razorpayKey = usableKey(found);
    if (!scriptLoaded || !window.Razorpay) {
        return { status: "error", message: "Couldn't load the payment gateway. Check your connection and try again." };
    }
    if (!razorpayKey) {
        return {
            status: "error",
            message: found?.mode === "test"
                ? "This is a test booking, but test payments aren't set up on the server (RAZOR_TEST_KEY_ID)."
                : "Payments aren't configured yet - check Settings > Payment.",
        };
    }

    return new Promise((resolve) => {
        const razorpay = new window.Razorpay({
            key: razorpayKey,
            order_id: orderId,
            amount: Math.round(Number(amount) * 100),
            currency: "INR",
            name: SITE_NAME,
            description,
            prefill,
            theme: { color: THEME_COLOR },
            handler: (razorpayResponse) => resolve({ status: "success", response: razorpayResponse }),
            modal: {
                ondismiss: () => resolve({ status: "dismissed" }),
            },
        });
        razorpay.on("payment.failed", () => resolve({ status: "failed" }));
        razorpay.open();
    });
};
